import { SeatsioSeatingChart } from '@seatsio/seatsio-react';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useMediaQuery } from 'react-responsive';
import styled, { useTheme } from 'styled-components';
import Icon from 'components/Icon';
import { POSDatePill, POSButton, POSIconButton } from 'components/POS';
import Spacer from 'components/Spacer';
import Toaster from 'components/Toaster';
import { useDebounceCallback } from 'hooks/useDebounceCallback';
import { AppContext } from 'shared/AppContext';
import { useCart } from 'shared/CartContext';
import { useHoldBestAvailable } from './gql/mutation/useHoldBestAvailable';
import { useReleaseBestAvailable } from './gql/mutation/useReleaseBestAvailable';
import { SeatSelectionModal } from './SeatSelectionModal';
import { TimerCountdown } from './TimerCountdown';

// Fullscreen Modal Overlay
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: ${(props) => props.marginLeft}px;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 10;
  display: ${(props) => (props.isVisible ? 'flex' : 'none')};
`;

const ModalContainer = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: ${(props) => props.width};
  max-width: ${(props) => props.maxWidth};
  background: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.background?.primary ||
        'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%)'
      : '#ffffff'};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 11;
`;

const HeaderContainer = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.03)'
      : 'rgba(0, 0, 51, 0.95)'};
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.1)'};
`;

const Header = styled.div`
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  min-height: 70px;
`;

const EventName = styled.span`
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 24px;
  font-weight: 700;
  line-height: 32px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.primary || '#ffffff'
      : '#ffffff'};

  @media (max-width: 768px) {
    font-size: 20px;
    line-height: 26px;
  }
`;

const CloseButtonWrapper = styled.div`
  position: absolute;
  top: 16px;
  right: 20px;
  z-index: 1000;
`;

const CloseButton = styled.button`
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(0, 0, 0, 0.05)'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.15)'
        : 'rgba(0, 0, 0, 0.1)'};
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.15)'
        : 'rgba(0, 0, 0, 0.08)'};
  }

  svg {
    color: ${(props) =>
      props.theme.name === 'dark'
        ? props.theme.colors?.text?.primary || '#ffffff'
        : '#000033'};
  }
`;

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  min-height: 400px;
`;

const OptionButtonsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  margin-top: ${(props) => (props.isMobile ? '35px' : '16px')};
  padding: 0 20px;
  justify-content: center;
`;

const CounterContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin: 16px 0;
`;

const CountText = styled.span`
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 32px;
  font-weight: 700;
  line-height: 40px;
  text-align: center;
  min-width: 48px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.primary || '#ffffff'
      : '#000033'};
`;

const FooterContainer = styled.div`
  background: ${(props) => props.theme.colors?.primary || '#FA4616'};
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  @media (max-width: 536px) {
    padding: 16px 12px;
    flex-wrap: wrap;
  }
`;

const LeftWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;

  @media (max-width: 536px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const ItemPrice = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SelectedCount = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: #ffffff;
`;

const CountBadge = styled.div`
  background: #ffffff;
  border-radius: 50%;
  color: #000033;
  width: 24px;
  height: 24px;
  font-weight: 700;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PriceText = styled.div`
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 28px;
  font-weight: 700;
  color: #ffffff;

  @media (max-width: 536px) {
    font-size: 24px;
  }
`;

const RightWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const AddToCartButton = styled(POSButton)`
  background: #ffffff !important;
  color: ${(props) => props.theme.colors?.primary || '#FA4616'} !important;
  border: none !important;
  padding: 16px 24px;
  font-size: 20px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.9) !important;
  }

  @media (max-width: 536px) {
    padding: 12px 16px;
    font-size: 18px;
  }
`;

const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
});

const CART_TIMEOUT = 600000;

const getTotalPrice = (selectedTicket) =>
  selectedTicket?.seats.reduce((acc, seat) => {
    const rawPrice = seat?.price ?? selectedTicket?.price;
    const numeric = parseFloat(String(rawPrice).replace('$', ''));
    return acc + (isNaN(numeric) ? 0 : numeric);
  }, 0) ?? 0;

export default function SeatsManagerModal({ isVisible, setIsVisible }) {
  const theme = useTheme();

  const {
    seatId,
    addTicketSeat,
    removeTicketSeat,
    chartRef,
    clearTicket,
    tickets,
    selected,
    event,
    bestTickets,
    setBestTickets,
    bestSeats,
    setBestSeats,
    clearBestTickets,
    selectedSeats,
    setSelectedSeats,
  } = useCart();

  const {
    state: { navIsExpanded },
  } = useContext(AppContext);

  const seatsio = JSON.parse(sessionStorage.getItem('seatsio'));
  const holdToken = seatsio?.holdToken;

  const selectedSeatId = localStorage.getItem('selectedTicket');
  const selectedTicket = tickets[selected]?.tickets?.find(
    (ticket) => ticket.id === +selectedSeatId
  );

  const seat_ids = selectedTicket?.seat_ids ?? [seatId];
  const seatIdKey = seat_ids.join('-');

  const holdBestAvailable = useHoldBestAvailable();
  const releaseBestAvailable = useReleaseBestAvailable();

  const [seatSelectionIsVisible, setSeatSelectionIsVisible] = useState(false);
  const [seatSelectionMode, setSeatSelectionMode] = useState('fromMap');

  const [isFetching, setIsFetching] = useState(false);
  const [isRendering, setIsRendering] = useState(true);

  const [selectedTicketCount, setSelectedTicketCount] = useState(
    selectedTicket?.seats ? selectedTicket.seats.length : 0
  );
  const [selectedTicketTotal, setSelectedTicketTotal] = useState(
    selectedTicket?.seats ? getTotalPrice(selectedTicket) : 0
  );

  const [seatObject, setSeatObject] = useState([]);
  const [pendingUpdates, setPendingUpdates] = useState({});

  // Toast state
  const [toastState, setToastState] = useState({
    isOpen: false,
    type: 'error',
    title: '',
    message: '',
  });

  const showToast = ({ type, title, message }) => {
    setToastState({ isOpen: true, type, title, message });
  };

  const closeToast = () => {
    setToastState((prev) => ({ ...prev, isOpen: false }));
  };

  const onAddTicket = () => {
    setBestTickets((prev) => {
      const prevCount = prev[seatIdKey] || 0;
      const newBestTickets = {
        ...prev,
        [seatIdKey]: prevCount + 1,
      };

      setPendingUpdates(newBestTickets);
      onHoldBestAvailable(prevCount + 1);
      return newBestTickets;
    });
  };

  const onRemoveTicket = () => {
    setBestTickets((prev) => {
      const prevCount = prev[seatIdKey] || 0;

      if (prevCount === 0) return prev;

      const newBestTickets = { ...prev, [seatIdKey]: prevCount - 1 };

      if (prev[seatIdKey] === 1) {
        onReleaseBestAvailable();

        setBestSeats((prev) => {
          const newBestSeats = new Set([...prev]);
          newBestSeats.delete(seatIdKey);
          return newBestSeats;
        });

        setSelectedSeats((prev) => {
          const newSelectedSeats = { ...prev };
          newSelectedSeats[seatIdKey] = [];
          return newSelectedSeats;
        });

        clearBestTickets(seat_ids);
        return { ...prev, [seatIdKey]: 0 };
      } else {
        onHoldBestAvailable(prev[seatIdKey] - 1);
        return newBestTickets;
      }
    });
  };

  const onFromMap = () => {
    setSeatSelectionMode('fromMap');
  };

  const onBestAvailable = () => {
    setSeatSelectionMode('bestAvailable');
  };

  const [timer, setTimer] = useState(CART_TIMEOUT);

  const seatCategory = useMemo(() => {
    return tickets[selected]?.tickets
      ?.filter(
        (ticket) => ticket.reserved_seat && ticket.ticket_tiers?.length > 0
      )
      ?.map((ticket) => {
        const ticketTypes = ticket.ticket_tiers.map((tier) => ({
          ticketType: tier.ticketType,
          label: tier.label,
          price: currency.format(
            tier.track_price ? tier.track_price : tier.price
          ),
          id: tier.id,
        }));
        return {
          category: ticket.reserved_seat_category,
          ticketTypes,
        };
      });
  }, [tickets, selected]);

  const isMobile = useMediaQuery({ query: '(max-width: 860px)' });

  const onClose = () => {
    if (chartRef.current?.chart) chartRef.current.chart.resetView();

    setTimeout(() => {
      setIsVisible(false);
    }, 1000);
  };

  const onObjectSelected = (seat) => {
    const selectedTicketType = seatCategory
      ?.find((category) => category.category === seat.category.label)
      ?.ticketTypes.find((type) => type.ticketType === seat.selectedTicketType);

    addTicketSeat(
      {
        id: seat.id,
        labels: seat.labels,
        category: seat.category,
        type: selectedTicketType?.id,
        price: selectedTicketType?.price,
      },
      seat_ids
    );
  };

  const onReleaseHoldSucceeded = (seats) => {
    if (!seats || seats.length === 0) return;

    const seat = seats.length ? seats[seats.length - 1] : seats;
    removeTicketSeat(
      {
        id: seat.id,
        labels: seat.labels,
        category: seat.category,
      },
      seat_ids
    );

    if (bestSeats.has(seat.id)) {
      setSelectedSeats((prev) => {
        const updatedSeats = prev[seatIdKey]?.filter(
          (selectedSeat) => selectedSeat.label !== seat.id
        );

        return {
          ...prev,
          [seatIdKey]: updatedSeats,
        };
      });
    }
  };

  const onHoldTokenExpired = () => {
    clearTicket();
  };

  useEffect(() => {
    if (isVisible) {
      setIsRendering(true);
    }
  }, [isVisible]);

  useEffect(() => {
    if (isVisible && selectedSeats[seatIdKey]?.length > 0) {
      setSeatObject(selectedSeats[seatIdKey]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  const intervalRef = useRef();

  useEffect(() => {
    const timerFn = () => {
      if (!holdToken) return;

      const storedTimer = localStorage.getItem(`timer - ${holdToken}`);
      const currentTime = new Date().getTime();
      let expiryTime;

      if (storedTimer) {
        expiryTime = parseInt(storedTimer, 10);
      } else {
        expiryTime = currentTime + CART_TIMEOUT;
        Object.keys(localStorage).forEach((key) => {
          if (key.includes('timer')) localStorage.removeItem(key);
        });
        localStorage.setItem(`timer - ${holdToken}`, expiryTime);
      }

      intervalRef.current = setInterval(() => {
        const timeLeft = expiryTime - new Date().getTime();
        if (timeLeft <= 0) {
          clearInterval(intervalRef.current);
          setIsVisible(false);
          setTimer(CART_TIMEOUT);
          clearTicket();
        } else {
          setTimer(timeLeft);
        }
      }, 1000);
    };

    if (isVisible) {
      timerFn();
    }

    return () => {
      clearInterval(intervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, setIsVisible, setTimer, clearTicket]);

  useEffect(() => {
    if (selectedTicket?.seats) {
      setSelectedTicketCount(selectedTicket.seats.length);
      setSelectedTicketTotal(getTotalPrice(selectedTicket));
    } else {
      setSelectedTicketCount(0);
      setSelectedTicketTotal(0);
    }
  }, [selectedTicket]);

  const fetchBestAvailableSeats = async (bestTicketsCount) => {
    setIsFetching(true);
    clearBestTickets(seat_ids);

    if (holdToken) {
      const response = await holdBestAvailable({
        hold_token: holdToken,
        seats_event_id: seat_ids[0],
        quantity: bestTicketsCount,
        selected_seats: selectedTicket?.seats
          ? selectedTicket?.seats?.map((seat) => seat.id)
          : null,
      });

      const seats = response?.data?.holdBestAvailable;

      setBestTickets((prev) => {
        const current = prev[seatIdKey] ?? null;
        if (current === seats?.length) return prev;

        return {
          ...prev,
          [seatIdKey]: seats?.length,
        };
      });

      if (seats && seats.length > 0 && seats[0]?.ticketType) {
        setBestSeats(
          new Set([...bestSeats, ...seats.map((seat) => seat.label)])
        );
      }

      const hasTicketType = !!seats[0]?.ticketType;

      if (seats && hasTicketType) {
        setIsRendering(true);
        setSelectedSeats((currentSelectedSeats) => {
          const updatedSeats = { ...currentSelectedSeats };

          updatedSeats[seatIdKey] = [
            ...seats.map((seat) => ({
              label: seat.label,
              ticketType: seat.ticketType,
            })),
          ];

          selectSeatsOnChart(updatedSeats[seatIdKey]);

          return updatedSeats;
        });
      }
    }

    setPendingUpdates({});
    setIsFetching(false);
  };

  const selectSeatsOnChart = async (seatIds) => {
    if (!chartRef.current?.chart || !seatIds?.length) return;

    const chart = chartRef.current.chart;

    await new Promise((resolve) => setTimeout(resolve, 1000));

    try {
      if (typeof chart.selectObjects === 'function') {
        await chart.selectObjects(seatIds);
      } else {
        for (const seatIdItem of seatIds) {
          await chart.selectObject(seatIdItem);
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      }
    } catch (err) {
      console.error('Selection failed:', err);
      showToast({
        type: 'error',
        title: 'Selection Error',
        message: 'Error selecting seats. Please try again.',
      });
    } finally {
      setIsRendering(false);
    }
  };

  const onReleaseBestAvailable = async () => {
    if (holdToken) {
      const response = await releaseBestAvailable({
        seats_event_id: seat_ids[0],
        selected_seats: selectedTicket?.seats
          ? selectedTicket?.seats?.map((seat) => seat.id)
          : null,
      });

      if (!response || response?.data?.releaseBestAvailable === null) {
        showToast({
          type: 'error',
          title: 'Release Error',
          message: 'Error releasing seats.',
        });
      }
    }
  };

  const onHoldBestAvailable = useDebounceCallback((bestTicketsCount) => {
    fetchBestAvailableSeats(bestTicketsCount);
  }, 1000);

  const marginLeft = isMobile ? 0 : !navIsExpanded ? 50 : 220;
  const modalWidth = isMobile ? '100vw' : '100%';
  const modalMaxWidth = isMobile
    ? '100vw'
    : !navIsExpanded
    ? 'calc(100vw - 50px)'
    : 'calc(100vw - 220px)';

  const CounterButtons = useMemo(
    () => (
      <CounterContainer>
        <POSIconButton
          variant="ghost"
          onClick={onRemoveTicket}
          disabled={bestTickets[seatIdKey] === 0 || isRendering || isFetching}
          style={{ width: 46, height: 40 }}
        >
          -
        </POSIconButton>
        <CountText>
          {pendingUpdates[seatIdKey] !== undefined
            ? pendingUpdates[seatIdKey]
            : bestTickets[seatIdKey] ?? 0}
        </CountText>
        <POSIconButton
          onClick={onAddTicket}
          disabled={isRendering || isFetching}
          style={{ width: 46, height: 40 }}
        >
          +
        </POSIconButton>
      </CounterContainer>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bestTickets, seatIdKey, isRendering, isFetching, pendingUpdates]
  );

  const OptionButtons = useMemo(
    () => (
      <OptionButtonsWrapper isMobile={isMobile}>
        <POSButton
          variant={seatSelectionMode === 'fromMap' ? 'primary' : 'outlined'}
          onClick={onFromMap}
          style={{
            padding: isMobile ? '12px 16px' : '12px 24px',
            fontSize: '16px',
          }}
        >
          From map
        </POSButton>
        <POSButton
          variant={
            seatSelectionMode === 'bestAvailable' ? 'primary' : 'outlined'
          }
          onClick={onBestAvailable}
          style={{
            padding: isMobile ? '12px 16px' : '12px 24px',
            fontSize: '16px',
          }}
        >
          Best Available
        </POSButton>
      </OptionButtonsWrapper>
    ),
    [seatSelectionMode, isMobile]
  );

  if (!isVisible) return null;

  return ReactDOM.createPortal(
    <>
      <ModalOverlay
        isVisible={isVisible}
        marginLeft={marginLeft}
        onClick={onClose}
      />
      <ModalContainer width={modalWidth} maxWidth={modalMaxWidth}>
        <HeaderContainer>
          <Header>
            {event?.start_date && (
              <POSDatePill
                startDate={event.start_date}
                endDate={event.end_date}
                isMultiDay={event.isMultiDay}
              />
            )}
            <EventName>{event?.name}</EventName>
          </Header>
        </HeaderContainer>

        <CloseButtonWrapper>
          <CloseButton onClick={onClose}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </CloseButton>
        </CloseButtonWrapper>

        <ContentWrapper>
          {event?.best_available && seat_ids.length === 1 && (
            <>
              {OptionButtons}
              {seatSelectionMode === 'bestAvailable' ? (
                CounterButtons
              ) : (
                <Spacer size={56} />
              )}
            </>
          )}

          {seat_ids && seat_ids.length > 0 && isVisible && (
            <SeatsioSeatingChart
              ref={chartRef}
              workspaceKey={process.env.REACT_APP_SEAT_WORKSPACE_KEY}
              events={seat_ids ?? ''}
              region="na"
              session={holdToken ? 'manual' : 'start'}
              onReleaseHoldSucceeded={onReleaseHoldSucceeded}
              onHoldFailed={(seats) => {
                console.error('Error holding seats', seats);
                seats.forEach((seat) => {
                  onReleaseHoldSucceeded(seat);
                });
                showToast({
                  type: 'error',
                  title: 'Hold Error',
                  message: 'Error holding seats.',
                });
                setIsFetching(false);
              }}
              onHoldTokenExpired={onHoldTokenExpired}
              holdToken={holdToken ? holdToken : ''}
              pricing={seatCategory}
              showFullScreenButton={false}
              onObjectSelected={onObjectSelected}
              selectedObjects={seatObject ?? []}
              mode={seatSelectionMode === 'bestAvailable' ? 'static' : 'normal'}
              onChartRendered={() => {
                setTimeout(() => {
                  setIsRendering(false);
                }, 1000);
              }}
              onChartRerenderingStarted={() => {
                setIsRendering(true);
              }}
            />
          )}

          <FooterContainer>
            <LeftWrapper>
              <ItemPrice>
                <SelectedCount>
                  <CountBadge>{selectedTicketCount ?? 0}</CountBadge>
                  Selected
                </SelectedCount>
                <PriceText>
                  {selectedTicketCount
                    ? '$' + selectedTicketTotal.toFixed(2)
                    : '$0.00'}
                </PriceText>
              </ItemPrice>
            </LeftWrapper>

            <RightWrapper>
              <TimerCountdown timer={timer} />
              <AddToCartButton
                type="button"
                onClick={() => setSeatSelectionIsVisible(true)}
              >
                Add to Cart
                <Icon
                  size={18}
                  icon="chevron"
                  color={theme.colors?.primary || '#FA4616'}
                  customStyle={{ transform: 'rotate(-90deg)' }}
                />
              </AddToCartButton>
            </RightWrapper>
          </FooterContainer>
        </ContentWrapper>

        <SeatSelectionModal
          isVisible={seatSelectionIsVisible}
          setIsVisible={setSeatSelectionIsVisible}
          onClose={onClose}
          seatIdKey={seatIdKey}
          seatCategory={seatCategory}
        />
      </ModalContainer>

      <Toaster
        isOpen={toastState.isOpen}
        onClose={closeToast}
        type={toastState.type}
        title={toastState.title}
        message={toastState.message}
        duration={3000}
      />
    </>,
    document.body
  );
}
